import React from 'react';
import type { TokenUsageGranularity, TokenUsageSeriesPoint } from '../types/tokenUsage';
import {
  formatBucketAxisLabel,
  formatBucketRangeLabel,
  formatCompactNumber,
  formatNumber,
  resolveAxisMax,
} from '../utils/tokenUsageFormat';
import styles from './AiUsage.module.css';

const CHART_HEIGHT = 260;
const PADDING_TOP = 16;
const PADDING_BOTTOM = 30;
const PADDING_LEFT = 52;
const PADDING_RIGHT = 12;
const GRID_LINES = 4;
const MAX_AXIS_LABELS = 9;

type TokenUsageChartProps = {
  series: TokenUsageSeriesPoint[];
  granularity: TokenUsageGranularity;
  locale: string;
  labels: {
    inputTokens: string;
    outputTokens: string;
    totalTokens: string;
    completions: string;
    empty: string;
  };
};

export const TokenUsageChart: React.FC<TokenUsageChartProps> = ({
  series,
  granularity,
  locale,
  labels,
}) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = React.useState(720);
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  React.useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }

    const applyWidth = () => setWidth(Math.max(320, element.clientWidth));
    applyWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', applyWidth);
      return () => window.removeEventListener('resize', applyWidth);
    }

    const observer = new ResizeObserver(applyWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const plotWidth = Math.max(1, width - PADDING_LEFT - PADDING_RIGHT);
  const plotHeight = CHART_HEIGHT - PADDING_TOP - PADDING_BOTTOM;
  const maxTotal = series.reduce((max, point) => Math.max(max, point.totalTokens), 0);
  const axisMax = resolveAxisMax(maxTotal);
  const hasData = maxTotal > 0;

  const slotWidth = series.length > 0 ? plotWidth / series.length : plotWidth;
  const barWidth = Math.max(3, Math.min(46, slotWidth * 0.62));
  const labelStride = Math.max(1, Math.ceil(series.length / MAX_AXIS_LABELS));

  const toY = (value: number) => PADDING_TOP + plotHeight - (value / axisMax) * plotHeight;
  const slotCenter = (index: number) => PADDING_LEFT + slotWidth * index + slotWidth / 2;

  const hoveredPoint = hoveredIndex !== null ? series[hoveredIndex] : undefined;
  const tooltipLeftRatio = hoveredIndex !== null && width > 0 ? slotCenter(hoveredIndex) / width : 0;

  return (
    <div className={styles.chartWrapper} ref={containerRef}>
      <svg
        className={styles.chart}
        width="100%"
        height={CHART_HEIGHT}
        viewBox={`0 0 ${width} ${CHART_HEIGHT}`}
        role="img"
        aria-label={labels.totalTokens}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {Array.from({ length: GRID_LINES + 1 }, (_, index) => {
          const value = (axisMax / GRID_LINES) * index;
          const y = toY(value);

          return (
            <g key={`grid-${index}`}>
              <line
                x1={PADDING_LEFT}
                x2={width - PADDING_RIGHT}
                y1={y}
                y2={y}
                className={styles.chartGridLine}
              />
              <text x={PADDING_LEFT - 8} y={y + 4} textAnchor="end" className={styles.chartAxisText}>
                {formatCompactNumber(Math.round(value), locale)}
              </text>
            </g>
          );
        })}

        {hasData ? series.map((point, index) => {
          const center = slotCenter(index);
          const x = center - barWidth / 2;
          const inputHeight = (point.inputTokens / axisMax) * plotHeight;
          const outputHeight = (point.outputTokens / axisMax) * plotHeight;
          const baseline = PADDING_TOP + plotHeight;
          const isHovered = hoveredIndex === index;

          return (
            <g
              key={point.key}
              onMouseEnter={() => setHoveredIndex(index)}
              onFocus={() => setHoveredIndex(index)}
            >
              <rect
                x={PADDING_LEFT + slotWidth * index}
                y={PADDING_TOP}
                width={slotWidth}
                height={plotHeight}
                className={`${styles.chartHoverZone} ${isHovered ? styles.chartHoverZoneActive : ''}`}
              />
              <rect
                x={x}
                y={baseline - inputHeight}
                width={barWidth}
                height={Math.max(0, inputHeight)}
                className={styles.chartBarInput}
                rx={2}
              />
              <rect
                x={x}
                y={baseline - inputHeight - outputHeight}
                width={barWidth}
                height={Math.max(0, outputHeight)}
                className={styles.chartBarOutput}
                rx={2}
              />
            </g>
          );
        }) : null}

        <line
          x1={PADDING_LEFT}
          x2={width - PADDING_RIGHT}
          y1={PADDING_TOP + plotHeight}
          y2={PADDING_TOP + plotHeight}
          className={styles.chartBaseline}
        />

        {series.map((point, index) => (
          index % labelStride === 0 ? (
            <text
              key={`label-${point.key}`}
              x={slotCenter(index)}
              y={CHART_HEIGHT - 10}
              textAnchor="middle"
              className={styles.chartAxisText}
            >
              {formatBucketAxisLabel(point, granularity, locale)}
            </text>
          ) : null
        ))}
      </svg>

      {!hasData ? <div className={styles.chartEmpty}>{labels.empty}</div> : null}

      {hoveredPoint ? (
        <div
          className={styles.chartTooltip}
          style={{
            left: `${tooltipLeftRatio * 100}%`,
            transform: `translateX(${tooltipLeftRatio > 0.7 ? '-100%' : tooltipLeftRatio < 0.3 ? '0' : '-50%'})`,
          }}
        >
          <strong>{formatBucketRangeLabel(hoveredPoint, granularity, locale)}</strong>
          <span className={styles.chartTooltipRow}>
            <i className={`${styles.legendSwatch} ${styles.legendSwatchInput}`} />
            {labels.inputTokens}
            <b>{formatNumber(hoveredPoint.inputTokens, locale)}</b>
          </span>
          <span className={styles.chartTooltipRow}>
            <i className={`${styles.legendSwatch} ${styles.legendSwatchOutput}`} />
            {labels.outputTokens}
            <b>{formatNumber(hoveredPoint.outputTokens, locale)}</b>
          </span>
          <span className={styles.chartTooltipRow}>
            {labels.totalTokens}
            <b>{formatNumber(hoveredPoint.totalTokens, locale)}</b>
          </span>
          <span className={styles.chartTooltipRow}>
            {labels.completions}
            <b>{formatNumber(hoveredPoint.completionCount, locale)}</b>
          </span>
        </div>
      ) : null}

      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <i className={`${styles.legendSwatch} ${styles.legendSwatchInput}`} />
          {labels.inputTokens}
        </span>
        <span className={styles.legendItem}>
          <i className={`${styles.legendSwatch} ${styles.legendSwatchOutput}`} />
          {labels.outputTokens}
        </span>
      </div>
    </div>
  );
};
